import type { ScenarioCallLedgerRow, ScenarioId } from './types';

/**
 * In-memory traceability ledger: the backend calls observed while each scenario is active,
 * keyed by the scenario's opaque `scenarioId`. The scenario façade drains and clears a
 * scenario's rows when it stops (attaching them to the stop event on a non-ok outcome only,
 * per the cardinality/PII policy), so entries live only for the scenario's own lifetime.
 *
 * Data-layer agnostic by design: the primitive only stores rows. The app-layer seam adapters
 * (e.g. the web feed's Redux publish middleware) decide which calls belong to a scenario and
 * record them here against the scenario's id.
 */
const ledgersByScenario = new Map<ScenarioId, ScenarioCallLedgerRow[]>();

/**
 * Appends a backend-call row to a scenario's ledger. Called by a seam adapter for each call it
 * attributes to the in-flight scenario. Recording against an unknown/already-drained scenario
 * id simply starts a fresh list — the façade drops it if that id never stops.
 */
export const recordScenarioCall = (scenarioId: ScenarioId, row: ScenarioCallLedgerRow): void => {
  const rows = ledgersByScenario.get(scenarioId);

  if (rows) {
    rows.push(row);
  } else {
    ledgersByScenario.set(scenarioId, [row]);
  }
};

/** Returns the rows recorded for a scenario so far (empty when none). Does not mutate. */
export const readScenarioCallLedger = (scenarioId: ScenarioId): readonly ScenarioCallLedgerRow[] =>
  ledgersByScenario.get(scenarioId) ?? [];

/** Drops a scenario's ledger. Called when the scenario stops so ids can't leak past their span. */
export const clearScenarioCallLedger = (scenarioId: ScenarioId): void => {
  ledgersByScenario.delete(scenarioId);
};
